
import { defineComponent, onBeforeMount, reactive } from 'vue'

// > components <
import { BsPage } from '@/modern/components'

// > submodules/payment-requests/components
import {
    PaymentRequestsBalanceWidget,
    PaymentRequestsHeaderAdditional,
    PaymentRequestsFilters,
    PaymentRequestsTransactionsModal,
    PaymentRequestsTopUpModal,
    PaymentRequestsTableColumnNumber,
    PaymentRequestsTableColumnDate,
    PaymentRequestsTableColumnCost,
    PaymentRequestsTableColumnStatus,
    PaymentRequestsTableColumnActions,
    PaymentRequestsPayModal,
    PaymentRequestsDetailsModal
} from '@/modern/modules/m-accounting-module/submodules/payment-requests/components'

// > services/types <
import type {
    PaymentRequest,
    GetPaymentRequestsParamsFilters,
    PaymentRequestsBalanceTransaction
} from '@/modern/modules/m-accounting-module/services/types/payment-requests.types'

// > services/payment-requests
import paymentRequestsAPI from '@/modern/modules/m-accounting-module/services/payment-requests.service'

import useNotifications from '@/common/composable/useNotifications'

const COLUMNS = [
    {
        key: 'id',
        name: 'Номер'    
    },
    {
        key: 'clientType',
        name: 'Тип'
    },
    {
        key: 'createdAt',
        name: 'Дата запроса'
    },
    {
        key: 'totalAmount',
        name: 'Сумма'
    },
    {
        key: 'receiverFullName',
        name: 'ФИО'
    },
    {
        key: 'number',
        name: 'Телефон'
    },
    {
        key: 'paymentMethod',
        name: 'Способ оплаты'
    },
    {
        key: 'statusId',
        name: 'Статус выплаты'
    }
]

export default defineComponent({
    components: {
        BsPage,
        PaymentRequestsBalanceWidget,
        PaymentRequestsHeaderAdditional,
        PaymentRequestsFilters,
        PaymentRequestsTransactionsModal,
        PaymentRequestsTopUpModal,
        PaymentRequestsTableColumnNumber,
        PaymentRequestsTableColumnDate,
        PaymentRequestsTableColumnCost,
        PaymentRequestsTableColumnStatus,
        PaymentRequestsTableColumnActions,
        PaymentRequestsPayModal,
        PaymentRequestsDetailsModal,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            isLoading: false,
            isFailed: false,
            list: [] as Array<PaymentRequest>,
            limit: 50,
            columns: [...COLUMNS] as Array<{ key: string; name: string }>,
            pagination: {
                page: 1,
                totalPages: 1,
                totalItems: 0
            },
            filters: {
                dateFrom: null as string | null,
                dateTo: null as string | null,
                id: null as string | null,
                statusIds: [] as number[],
                transferTypeId: null as number | null,
                totalAmountFrom: null as string | null,
                totalAmountTo: null as string | null,
                instanceName: null as string | null
            }
        })

        const transactionsState = reactive({
            isOpened: false,
            isLoading: false,
            isFailed: false,
            //
            amount: null as string | null,
            id: null as number | null,
            transactionHistory: [] as PaymentRequestsBalanceTransaction[]
        })

        const topUpState = reactive({
            isOpened: false,
        })

        const payState = reactive({
            isOpened: false,
            paymentRequest: null as PaymentRequest | null
        })

        const declineState = reactive({
            isLoading: false,
        })

        const detailsState = reactive({
            isOpened: false,
            paymentRequest: null as PaymentRequest | null
        })

        const statisticsState = reactive({
            isLoading: false,
            isFailed: false,
            //
            totalAccepted: 0,
            totalCount: 0,
            totalSpendings: '0',
            totalWaiting: 0
        })

        const onOpenTransactions = () => {
            transactionsState.isOpened = true
        }

        const onCloseTransactions = () => {
            transactionsState.isOpened = false
        }

        const onOpenTopUp = () => {
            topUpState.isOpened = true
        }

        const onCloseTopUp = () => {
            topUpState.isOpened = false
        }

        const onBalanceDeposited = () => {
            topUpState.isOpened = false
            loadBalance()
        }

        const onOpenPay = (paymentRequest: PaymentRequest) => {
            payState.paymentRequest = paymentRequest
            payState.isOpened = true
        }

        const onClosePay = () => {
            payState.isOpened = false
            payState.paymentRequest = null
        }

        const onPayed = () => {
            onCloseDetails()

            payState.isOpened = false
            loadBalance()
            loadPaymentRequests(state.pagination.page)
        }

        const onDecline = async (paymentRequest: PaymentRequest) => {
            const answer = confirm(`Вы действительно хотите отказать?`)
            if (!answer) return

            try {
                declineState.isLoading = true

                await paymentRequestsAPI.declinePaymentRequest(paymentRequest.id)

                notifications.notify({ type: 'success', title: 'Отказано' })

                onCloseDetails()
                loadPaymentRequests(state.pagination.page)
            } catch (error) {
                console.error(error)
            } finally {
                declineState.isLoading = false
            }
        }

        const onOpenDetails = (paymentRequest: PaymentRequest) => {
            detailsState.paymentRequest = paymentRequest
            detailsState.isOpened = true
        }

        const onCloseDetails = () => {
            detailsState.isOpened = false
            detailsState.paymentRequest = null
        }

        const loadBalance = async () => {
            transactionsState.amount = null
            transactionsState.id = null
            transactionsState.transactionHistory = []

            try {
                transactionsState.isLoading = true

                const response = await paymentRequestsAPI.getBalance()

                transactionsState.amount = response.data.data.amount
                transactionsState.id = response.data.data.id
                transactionsState.transactionHistory = response.data.data.transactionHistory

                transactionsState.isFailed = false
            } catch (error) {
                console.error(error)
                transactionsState.isFailed = true
            } finally {
                transactionsState.isLoading = false
            }
        }

        const loadPaymentRequests = async (page: number) => {
            state.pagination.page = page

            try {
                state.isLoading = true

                const response = await paymentRequestsAPI.getPaymentRequests({
                    page: state.pagination.page,
                    limit: state.limit,
                    dateFrom: state.filters.dateFrom,
                    dateTo: state.filters.dateTo,
                    statusIds: state.filters.statusIds,
                    transferTypeId: state.filters.transferTypeId,
                    totalAmountFrom: state.filters.totalAmountFrom,
                    totalAmountTo: state.filters.totalAmountTo,
                    instanceName: state.filters.instanceName
                })

                state.list = response.data.data
                state.pagination.totalPages = response.data.meta.last_page
                state.pagination.totalItems = response.data.meta.total

                state.isFailed = false
            } catch (error) {
                console.error(error)
                state.isFailed = true
            } finally {
                state.isLoading = false
            }
        }

        const loadStatistics = async () => {
            try {
                statisticsState.isLoading = true

                const response = await paymentRequestsAPI.getPaymentRequestsStatistics()
                statisticsState.totalAccepted = response.data.data.totalAccepted
                statisticsState.totalCount = response.data.data.totalCount
                statisticsState.totalSpendings = response.data.data.totalSpendings
                statisticsState.totalWaiting = response.data.data.totalWaiting

                statisticsState.isFailed = false
            } catch (error) {
                console.error(error)
                statisticsState.isFailed = true
            } finally {
                statisticsState.isLoading = false
            }
        }
        
        onBeforeMount(() => {
            loadBalance()
            loadPaymentRequests(1)
            loadStatistics()
        })

        const onFiltersSubmit = (params: GetPaymentRequestsParamsFilters) => {
            state.filters.id = params.id as string | null
            state.filters.dateFrom = params.dateFrom as string | null
            state.filters.dateTo = params.dateTo as string | null
            state.filters.statusIds = params.statusIds as number[]
            state.filters.transferTypeId = params.transferTypeId as number | null
            state.filters.totalAmountFrom = params.totalAmountFrom as string | null
            state.filters.totalAmountTo = params.totalAmountTo as string | null

            loadPaymentRequests(1)
        }

        const onFiltersReset = () => {
            state.filters.id = null
            state.filters.dateFrom = null
            state.filters.dateTo = null
            state.filters.statusIds = []
            state.filters.transferTypeId = null
            state.filters.totalAmountFrom = null
            state.filters.totalAmountTo = null

            loadPaymentRequests(1)
        }

        return {
            COLUMNS,
            state,
            transactionsState,
            topUpState,
            payState,
            detailsState,
            statisticsState,
            loadPaymentRequests,
            onOpenTransactions,
            onCloseTransactions,
            onOpenTopUp,
            onCloseTopUp,
            onBalanceDeposited,
            onFiltersSubmit,
            onFiltersReset,
            onOpenPay,
            onClosePay,
            onPayed,
            onDecline,
            onOpenDetails,
            onCloseDetails
        }
    }
})
